export const ROLES = [
    'SUPER_ADMIN',
    'DIRECTION',
    'AGENT_ACCUEIL',
    'MEDECIN',
    // ADR 0004 — un par agence et par antenne : il repond de la marche de son
    // site, en plus d'y exercer.
    'RESPONSABLE_SITE',
    // ADR 0004 — UNE SEULE personne dans tout AFS : le referent medical, qui
    // juge si une commande de medicaments est medicalement justifiee.
    'MEDECIN_CHEF',
    'PHARMACIEN_FRONT',
    'PHARMACIEN_BACK',
    'RESPONSABLE_ACHAT',
    'COMPTABLE',
    'CHARGE_CLIENTELE',
    'AGENT_PEC',
    // Caisse des patients externes (spécification du 16/10, R16) : un rôle de
    // site qui encaisse et clôture, sans rien de médical. Hors CCT : écart à acter.
    'CAISSIER',
    // Décision 449 — le chef de centre qui n'est pas médecin : un rôle de site
    // qui gère les comptes de SON centre et lit sa file et sa caisse, sans rien
    // de médical. Hors CCT : écart à acter (D1 bis), comme la caissière.
    'CHEF_CENTRE',
    // Rôles externes — périmètre volontairement très restreint.
    'RH_ADHERENT',
    'PRESTATAIRE',
];
/** Rôles pour lesquels le 2FA est obligatoire (CCT § 5.1). */
export const ROLES_2FA_OBLIGATOIRE = ['SUPER_ADMIN', 'DIRECTION'];
/** Rôles externes à l'organisation AFS : jamais d'accès aux données d'un autre adhérent. */
export const ROLES_EXTERNES = ['RH_ADHERENT', 'PRESTATAIRE'];
/**
 * Rôles dont l'espace n'existe pas encore : aucun compte ne s'y crée ni ne
 * s'y bascule, et un compte qui en porterait un n'ouvre rien (décision 115).
 *
 * Le portail RH (Phase 3) doit montrer à un RH les seuls salariés de SON
 * entreprise. Or un compte ne porte aucun lien vers une entreprise : rien,
 * côté serveur, ne saurait borner ce qu'il lit. Lui donner `dns:read` ou
 * `patient:read` aujourd'hui, c'est lui ouvrir toutes les entreprises
 * (rapport S159). Le rôle ne revient qu'avec ce lien et son filtre.
 */
export const ROLES_NON_OUVERTS = ['RH_ADHERENT'];
/** Les rôles de base qu'un écran propose : tous, sauf les non ouverts. */
export const ROLES_OUVERTS = ROLES.filter((role) => !ROLES_NON_OUVERTS.includes(role));
/** Le refus d'un rôle non ouvert, ou `null` s'il s'attribue. */
export function motifRoleNonOuvert(base) {
    return ROLES_NON_OUVERTS.includes(base)
        ? `Le rôle « ${LIBELLE_ROLE[base]} » n’est pas encore ouvert : il ne s’attribue à aucun compte.`
        : null;
}
const PARTOUT = {
    sortieDeFile: true,
    metier: true,
    entree: 'partout',
    perimetre: 'zone',
    duplicata: true,
};
const PRATICIEN = {
    metier: true,
    entree: 'antenne',
    dotation: true,
    perimetre: 'zone',
    duplicata: true,
};
const AUCUN_GESTE = {
    sortieDeFile: false,
    metier: false,
    entree: 'jamais',
    dotation: false,
    perimetre: 'centre',
    duplicata: false,
};
const GESTES_DE_LA_BASE = {
    SUPER_ADMIN: { ...PARTOUT, dotation: false },
    DIRECTION: { ...PARTOUT, dotation: false },
    AGENT_ACCUEIL: { ...PARTOUT, metier: false, dotation: true },
    MEDECIN: { ...PRATICIEN, sortieDeFile: false },
    RESPONSABLE_SITE: { ...PRATICIEN, sortieDeFile: true },
    MEDECIN_CHEF: { ...PRATICIEN, sortieDeFile: true },
    PHARMACIEN_FRONT: { ...PARTOUT, dotation: true },
    PHARMACIEN_BACK: { ...PARTOUT, dotation: false },
    RESPONSABLE_ACHAT: { ...PARTOUT, dotation: false },
    COMPTABLE: { ...PARTOUT, dotation: false },
    CHARGE_CLIENTELE: { ...PARTOUT, dotation: false },
    AGENT_PEC: { ...PARTOUT, dotation: true },
    CAISSIER: { ...PARTOUT, dotation: true },
    // Décision 449 : il lit la file et la caisse de son centre, il n'y fait
    // aucun geste et ne voit aucune fiche.
    // D1 ter : « de son centre », pas de toute la zone.
    CHEF_CENTRE: AUCUN_GESTE,
    RH_ADHERENT: { ...PARTOUT, dotation: false },
    PRESTATAIRE: { ...PARTOUT, dotation: false },
};
/** Les gestes d'une base ; les plus restrictifs si la base manque ou est inconnue. */
function gestesDe(base) {
    if (base === null || base === undefined)
        return AUCUN_GESTE;
    return Object.hasOwn(GESTES_DE_LA_BASE, base) ? GESTES_DE_LA_BASE[base] : AUCUN_GESTE;
}
function basesSans(geste) {
    return ROLES.filter((role) => !geste(gestesDe(role)));
}
/**
 * Décision 279 (TL, 28/10) : le métier du patient — `Personne.fonction`, le
 * poste qu'il occupe dans son entreprise — ne se montre pas à l'accueil ; il
 * se montre chez le médecin. Lu sur le rôle de BASE : un dérivé de l'accueil
 * (`ACCUEIL_CAISSE`…) est de l'accueil. L'API ne l'envoie pas à ces rôles.
 */
export const ROLES_SANS_METIER_DU_PATIENT = basesSans((gestes) => gestes.metier);
export function voitLeMetierDuPatient(base) {
    return gestesDe(base).metier;
}
/**
 * Décision 315 (TL, 28/10) : seul l'accueil sort un patient de la file
 * d'attente — « Retirer de la file » (R10) et « Annuler » une entrée. Le
 * médecin appelle, prend, met « ne répond pas », remet dans la file et
 * termine, mais ne fait sortir personne. Lu sur le rôle de BASE : un dérivé
 * de MEDECIN est un médecin. `file_attente:write`, que le médecin tient pour
 * appeler, ne suffit donc pas à ces gestes. Médecin-chef et responsable de
 * site gardent les leurs.
 */
export const ROLES_SANS_SORTIE_DE_FILE = basesSans((gestes) => gestes.sortieDeFile);
export function peutSortirDeLaFile(base) {
    return gestesDe(base).sortieDeFile;
}
/**
 * Décision 354 (TL, 30/10) : « La carte indique une autre offre » (R4) est un
 * geste de l'accueil, au comptoir. La chargée change l'offre elle-même ; la
 * Direction, les médecins et la caisse n'ont rien à signaler. Lu sur le rôle
 * de BASE (ADR 0006) : « Accueil + caisse » est de l'accueil. Masque
 * seulement : la route et ses droits ne changent pas.
 */
export const ROLES_SIGNALEMENT_ECART_OFFRE = ['AGENT_ACCUEIL'];
export function signaleUnEcartDOffre(base) {
    return ROLES_SIGNALEMENT_ECART_OFFRE.includes(base);
}
/**
 * RG-PA-07 nomme « le rôle MEDECIN ». Depuis l'ADR 0004 la matrice en compte
 * trois — un médecin, un responsable de site, un Médecin Chef — mais ce sont
 * trois praticiens, pas trois métiers : c'est la qualité de médecin qui ouvre
 * le dossier. C'est aussi la seule base dont un rôle dérivé peut tenir le
 * dossier médical (ADR 0006, RG-SEC-ROLE-MEDICAL).
 *
 * ⚠️ Ajouter un rôle ici est une DÉCISION MÉTIER, à porter dans un ADR.
 */
export const ROLES_MEDICAUX = ['MEDECIN', 'RESPONSABLE_SITE', 'MEDECIN_CHEF'];
/**
 * AJ-06 (TL, 28/10) : « Le médecin peut noter l'entrée seulement s'il
 * travaille dans une antenne. » Une antenne n'a qu'un médecin, pas d'accueil
 * (`effectifsProposes`) : il y note lui-même l'entrée. En agence, l'accueil
 * la note. Vaut pour les trois praticiens (`ROLES_MEDICAUX`), lu sur le rôle
 * de BASE (ADR 0006) et sur le type du site du POSTE DU JOUR (ADR 0002), pas
 * celui du compte. `file_attente:write`, que le médecin tient pour appeler,
 * ne suffit donc pas. L'accueil, et ses dérivés, ne changent pas.
 */
export const ROLES_ENTREE_EN_ANTENNE_SEULEMENT = ROLES.filter((role) => gestesDe(role).entree === 'antenne');
export const MOTIF_ENTREE_EN_AGENCE = 'En agence, l’entrée se note à l’accueil.';
/** `typeSite` : celui du poste du jour ; `null` sans poste — un praticien n'y note rien. */
export function peutNoterUneEntree(base, typeSite) {
    const entree = gestesDe(base).entree;
    return entree === 'partout' || (entree === 'antenne' && typeSite === 'ANTENNE');
}
/**
 * Rôles dont le périmètre couvre les 11 sites.
 *
 * Ce ne sont pas des « super-utilisateurs » : leur métier est simplement
 * transverse. Le comptable rapproche des encaissements de toutes les agences,
 * le chargé de clientèle suit des entreprises et non des sites, le
 * responsable des achats commande pour l'ensemble.
 *
 * Ces comptes portent `siteId: null` en base — ce qui ne veut pas dire
 * « aucun site », mais « tous ». Un compte NON transverse sans site est une
 * anomalie de données : le calcul de périmètre lui rend un périmètre vide,
 * jamais un accès complet.
 *
 * `PHARMACIEN_BACK` en fait partie : le stock central et le dispatch
 * inter-sites n'appartiennent à aucune agence — c'est justement leur objet
 * (arbitré avec le Tech Lead). Le seed le rattachait à une agence ; il porte
 * désormais `siteId: null` comme les autres transverses.
 */
export const ROLES_TRANSVERSES = [
    'SUPER_ADMIN',
    'DIRECTION',
    'RESPONSABLE_ACHAT',
    'COMPTABLE',
    'CHARGE_CLIENTELE',
    'PHARMACIEN_BACK',
];
/**
 * Rôles rattachés à un site — ceux qui prennent leur poste quelque part.
 *
 * Dérivés, jamais listés à la main : un rôle ajouté à la matrice est « de
 * site » dès lors qu'il n'est ni transverse ni externe. C'est la liste des
 * métiers qu'un site peut héberger et pour lesquels la Direction déclare un
 * effectif (ADR 0001), et celle des rôles qui choisiront leur site de
 * travail à la connexion (ADR 0002).
 */
export const ROLES_DE_SITE = ROLES.filter((role) => !ROLES_TRANSVERSES.includes(role) && !ROLES_EXTERNES.includes(role));
/**
 * Les métiers qu'un site déclare dans sa dotation (ADR 0001) : les rôles de
 * site, sauf le chef de centre (D17, décision 449) — il ne tient ni box ni
 * guichet, et un poste prévu pour lui fausserait l'écart dotation/en poste.
 */
export const ROLES_DE_DOTATION = ROLES.filter((role) => gestesDe(role).dotation);
export function entreDansLaDotation(role) {
    return gestesDe(role).dotation;
}
/**
 * D1 ter (décision 449) : le chef de centre non médecin lit la file, les box,
 * le registre et les écrans d'appel de SON centre, pas de toute la zone.
 * `'centre'` aussi pour une base absente : le périmètre le plus étroit.
 */
export function perimetreDeLaBase(base) {
    return gestesDe(base).perimetre;
}
/**
 * Complément 449 (S1113) : « en lecture seule » (D1 ter) — le chef de centre
 * non médecin lit les reçus de son centre, il n'en réimprime pas de duplicata.
 * Les autres lecteurs de la caisse (Direction, responsable de site) gardent le geste.
 */
export function peutReimprimerUnRecu(base) {
    return gestesDe(base).duplicata;
}
export const PERMISSIONS = [
    // Socle
    'utilisateur:read',
    'utilisateur:write',
    /**
     * Gérer les comptes de SON centre, comme chef de centre (décision 449) :
     * créer un agent d'accueil, un médecin ou un « Accueil + caisse » rattaché
     * à son site, jamais un autre chef. Dans la seule matrice de base
     * CHEF_CENTRE : elle ne vient que des deux rôles natifs du chef (RG-SEC-ROLE-CENTRE,
     * `rules/roles-derives.ts`), et ne sert qu'avec la désignation sur le site
     * (`Site.responsableId`, ADR 0004 révisée le 02/11).
     */
    'utilisateur:write_centre',
    'configuration:read',
    'configuration:write',
    'audit:read',
    /**
     * Télécharger une archive de sauvegarde chiffrée du serveur (décision 96).
     * Le Super administrateur seul : l'archive contient toute la base et le
     * `.env`. Aucun rôle d'une autre base ne peut la recevoir, même dérivé
     * (RG-SEC-ROLE-SAUVEGARDE, `rules/roles-derives.ts`).
     */
    'sauvegarde:telecharger',
    /**
     * Rôles définis (ADR 0006) : lire la liste des rôles et leurs permissions,
     * en créer, les modifier, les désactiver. `role:write` est le pouvoir de
     * redistribuer des droits — borné par RG-SEC-ROLE-ELEVATION : on ne donne
     * pas ce qu'on n'a pas.
     */
    'role:read',
    'role:write',
    /**
     * Référentiel des 11 sites — noms, codes, zones, agences et antennes.
     *
     * Accordée à tous les rôles INTERNES : dès qu'un écran affiche ou demande
     * un site — périmètre d'un compte, file d'attente, import DNS — il lui
     * faut cette liste. La refuser obligerait chaque module à la véhiculer
     * autrement, et c'est ainsi qu'on finit par la recopier.
     *
     * Refusée aux rôles externes (`RH_ADHERENT`, `PRESTATAIRE`) : leur
     * périmètre est volontairement très restreint, et l'organisation interne
     * d'AFS ne les regarde pas.
     */
    'site:read',
    /**
     * Référentiel des sites en ÉCRITURE — ouvrir, modifier, fermer une agence
     * ou une antenne, déclarer son effectif par rôle, créer une zone
     * (ADR 0001). La Direction décide d'ouvrir une agence ; le Super Admin
     * non technique du CCT § 6 corrige un libellé ou un rattachement un
     * dimanche, sans la mobiliser. `configuration:write` ne couvre pas ce
     * cas : elle règle des seuils, pas l'organisation.
     */
    'site:write',
    // M1 — Parcours Patient
    'patient:read',
    'patient:write',
    /**
     * Confirmer, infirmer, ou revenir sur une infirmation — la fiche créée au
     * guichet, vérifiée ensuite contre le DNS (CP-13). Distincte de
     * `dns:validate`, qui valide un FICHIER importé : ici on tranche une
     * PERSONNE, et l'infirmation ferme sa couverture.
     */
    'patient:confirm',
    /**
     * Compléter la fiche administrative : date de naissance, sexe, et pour un
     * salarié sa fonction, son lieu d'affectation, sa date d'entrée. Pas
     * l'identité qui sert à rapprocher le DNS (nom, prénom, matricule), ni le
     * rattachement. Recette du 28/09 : l'accueil et la chargée complètent la
     * fiche, sans donner à la chargée l'enrôlement au guichet (`patient:write`).
     * Referme en partie l'écart C3 d'ARB-01.
     */
    'patient:update',
    /**
     * Confirmer un conjoint ou un enfant sur pièce justificative, au guichet
     * (décision du Tech Lead, 28/09) : acte de mariage, acte de naissance ou
     * livret de famille présenté, sa photo jointe à la fiche, et le compte des
     * ayants droit dans ce que le salarié a déclaré au DNS. Ouvre aussi le
     * dépôt et la lecture de cette photo.
     *
     * Distincte de `patient:confirm` : confirmer un SALARIÉ contre le DNS reste
     * à la chargée de clientèle, comme trancher un ayant droit au-delà du DNS.
     * Accordée à tout l'accueil ; un rôle dérivé peut la retirer (ADR 0006).
     */
    'ayant_droit:confirm_piece',
    /**
     * Retirer un conjoint ou un enfant DÉJÀ CONFIRMÉ de la couverture de son
     * salarié, avec un motif obligatoire (séparation, décès, âge, autre), et
     * annuler ce retrait (décision du Tech Lead, 29/09). Il n'est plus couvert
     * dès maintenant ; les soins passés restent acquis ; rien n'est supprimé.
     *
     * Distincte de `patient:confirm`, qui infirme une fiche encore « à
     * confirmer » : retirer quelqu'un que l'on a couvert est une autre
     * décision, qui se donne ou se retire à part. Nativement à la seule chargée
     * de clientèle ; la Direction peut la recevoir par un rôle dérivé
     * (ADR 0006).
     */
    'ayant_droit:retirer',
    /**
     * Déclarer à la main le départ d'un salarié — réunion du 15/10 : quand
     * l'entreprise écrit qu'il a quitté ses effectifs, ses droits s'arrêtent
     * sans attendre le DNS suivant. Le geste s'annule.
     */
    'patient:depart',
    /**
     * Fusionner deux fiches qui désignent la même personne, écarter un faux
     * doublon, défaire une fusion. Une décision d'IDENTITÉ : elle n'est pas au
     * guichet, où l'on sert, mais chez qui tient le référentiel.
     */
    'patient:merge',
    'eligibilite:check',
    'file_attente:read',
    'file_attente:write',
    'rdv:read',
    'rdv:write',
    'dossier_medical:read',
    'dossier_medical:write',
    'dns:read',
    'dns:import',
    'dns:validate',
    // Référentiel des entreprises adhérentes — le poste de la chargée de
    // clientèle (CCT § 6). La suspension reste à part : RG-FIN-08.
    'adherent:read',
    'adherent:write',
    'offre:write',
    // M2 — Pharmacie
    'stock:read',
    'dispensation:write',
    'stock_central:write',
    'dispatch:write',
    'inventaire:write',
    'commande:read',
    'commande:write',
    'commande:validate',
    // ADR 0004 — la validation MÉDICALE, distincte de la validation
    // financière ci-dessus. Une commande cliniquement justifiée peut être
    // refusée faute de trésorerie ; une commande finançable peut être
    // médicalement absurde. Les fondre perdrait qui a jugé quoi.
    'commande:validate_medical',
    // M3 — Recouvrement
    'cotisation:read',
    /**
     * Vue « DNS et salaires » (décisions 130 et 131 du TL, 20/10) : la masse
     * salariale de chaque entreprise, l'historique de ses DNS et le salaire de
     * chaque personne d'un import. Seuls la chargée de clientèle, la Comptable
     * et la Direction la détiennent, jamais le Super administrateur. Parmi les
     * rôles dérivés, seul celui de la chargée la reçoit par défaut (décision
     * 142), et le Super admin peut la lui retirer (RG-SEC-ROLE-SALAIRE,
     * `rules/roles-derives.ts`).
     *
     * Elle ouvre aussi les salaires du module DNS : la masse au rapport d'un
     * import et la colonne salaire de la grille. `cotisation:read` ne les ouvre
     * plus (décision 130 : le Super admin la détient et ne voit aucun salaire).
     */
    'salaire:read',
    'encaissement:write',
    'encaissement:modify',
    'relance:read',
    'relance:validate',
    'adherent:suspend',
    'negociation:write',
    // M4 — PEC
    'pec:read',
    'pec:emit',
    'pec:derogation',
    'pec:config',
    'pec:verify_qr',
    'rapprochement:write',
    /**
     * Caisse des patients non couverts (RG-PA-02, spécification du 16/10).
     * Rien à voir avec `encaissement:*`, qui parle des cotisations des
     * entreprises (M3) : une permission neuve par geste.
     *
     *   caisse:encaisser        encaisser, réimprimer, annuler SES reçus du jour
     *   caisse:cloturer         clôturer SA caisse du jour
     *   caisse:read             le registre de la caisse (Direction, responsable et chef de centre)
     *   caisse:annuler_cloture  annuler un reçu après la clôture (Direction)
     *   caisse:tarif            régler la grille du tarif (Direction)
     */
    'caisse:encaisser',
    'caisse:cloturer',
    'caisse:read',
    'caisse:annuler_cloture',
    'caisse:tarif',
    /**
     * Signaler un problème (décision 146) : créer n'exige aucune permission ;
     * lire tous les signalements et les traiter, le Super administrateur seul
     * par défaut — ajoutables à la Direction depuis Rôles & accès.
     */
    'signalement:read',
    'signalement:manage',
    /**
     * Activité des médecins (décision 165, conception S240) : combien de
     * patients chaque praticien a reçus, par agence et par jour, et la durée
     * moyenne de ses consultations. Des comptes et des durées, rien de médical.
     * Distincte de `dashboard:read`, qui l'ouvrirait aux comptables et aux
     * chargées de clientèle et la fermerait aux médecins. Un rôle de site ne
     * voit que son agence (`modules/activite-medecins`).
     */
    'activite_medecin:read',
    // Transverse
    'dashboard:read',
    'export:data',
    /**
     * Décision 458 (TL, 03/11) — le registre d'un mois pour UNE entreprise, en
     * fichier Excel, que la chargée remet elle-même à l'entreprise. Il porte les
     * jours de repos médical (donnée de santé remise à l'employeur, écart au CCT
     * à acter dans ARB-01), jamais un motif ni une observation. Distincte de
     * `export:data` (l'export de masse, Direction seule) : chargée de clientèle
     * et Direction. Jamais au chef de centre (`PERMISSIONS_INTERDITES_AU_CHEF`).
     */
    'registre:export_entreprise',
];
/**
 * Matrice rôle → permissions.
 *
 * Principe : fermé par défaut. Une permission absente est refusée.
 * Toute modification de cette matrice est une décision métier : elle se
 * justifie dans la PR en citant la matrice du CCT concernée.
 */
export const PERMISSIONS_PAR_ROLE = {
    // Décision 418 (TL, 30/10) : le Super administrateur peut TOUT faire,
    // dossier médical compris, et il est le seul. Ses permissions se calculent —
    // toutes celles que le code connaît, celles d'une PR future comprises — au
    // lieu de se lister : une liste oublierait la prochaine permission ajoutée.
    // Écart au CCT (§ 6, RG-PA-07) à acter dans ARB-01 : l'accès au contenu
    // médical hors médecin, journalisé à chaque lecture (`apps/api/src/core/rbac/acces-sensibles.ts`).
    SUPER_ADMIN: PERMISSIONS,
    DIRECTION: [
        'site:read',
        'site:write', // ADR 0001 : la Direction ouvre et ferme les agences et antennes
        'utilisateur:read',
        'utilisateur:write',
        'role:read',
        'role:write', // ADR 0006 : borné à ce qu'elle détient elle-même
        'configuration:read', // constate les seuils ; le réglage reste au Super admin
        'audit:read',
        'patient:read',
        'patient:merge',
        'file_attente:read',
        'rdv:read',
        'dossier_medical:read', // lecture seule — matrice CCT_01 § 2.1
        'dns:read',
        'adherent:read',
        'offre:write', // ADR 0005 : le référentiel des offres est tenu par la Direction
        'stock:read',
        'commande:read',
        'commande:validate',
        'cotisation:read',
        'salaire:read', // décision 130 : chargée, Comptable, Direction seules
        'encaissement:modify', // RG-FIN-05 : Direction seule
        'relance:read',
        'relance:validate',
        'adherent:suspend', // RG-FIN-08 : jamais automatique — Direction et chargée (décision 364)
        'pec:read',
        'pec:derogation', // RG-PEC-05 : Direction seule
        // Caisse (spécification du 16/10, R12, R14, R16) : elle lit, annule après
        // clôture et règle la grille — `configuration:write` reste au Super admin.
        'caisse:read',
        'caisse:annuler_cloture',
        'caisse:tarif',
        'activite_medecin:read', // décision 165 : chaque centre
        'dashboard:read',
        'export:data',
        'registre:export_entreprise', // décision 458
    ],
    AGENT_ACCUEIL: [
        'site:read',
        'patient:read',
        'patient:write',
        'patient:update',
        'ayant_droit:confirm_piece', // décision du 28/09 : tout l'accueil
        'eligibilite:check',
        'file_attente:read',
        'file_attente:write',
        'rdv:read',
        'rdv:write',
        // Pas de dossier_medical:* — RG-PA-07.
    ],
    MEDECIN: [
        'site:read',
        'patient:read',
        'file_attente:read',
        'file_attente:write',
        'rdv:read',
        'dossier_medical:read',
        'dossier_medical:write', // RG-PA-07 : seul rôle en écriture
        'stock:read', // consultation du stock avant prescription — MVP-PH-03
        'activite_medecin:read', // décision 165 : son agence, ses confrères compris
    ],
    /**
     * ADR 0004 — un par agence et par antenne.
     *
     * Il détient TOUT ce que détient `MEDECIN`, et davantage : un responsable
     * qui ne pourrait pas consulter un dossier serait un responsable qui ne
     * soigne pas. L'inclusion est vérifiée par un test — une permission
     * ajoutée à `MEDECIN` et oubliée ici produirait un supérieur moins capable
     * que son équipe, et personne ne le verrait avant de s'y heurter.
     *
     * ⚠️ Cette liste est VOLONTAIREMENT INCOMPLÈTE. Les prérogatives du
     *    responsable de site restent à recenser avec AFS (ADR 0004). Chaque
     *    ajout se documente là-bas avec qui l'a demandé et ce qu'il remplace.
     */
    RESPONSABLE_SITE: [
        'site:read',
        'patient:read',
        'file_attente:read',
        'file_attente:write',
        'rdv:read',
        'dossier_medical:read',
        'dossier_medical:write',
        'stock:read',
        // Il répond de la marche du site : voir ce qui y est commandé en fait
        // partie. Ne s'exerce que sur SON site — voir `rules/responsabilite.ts`.
        'commande:read',
        // La caisse de son site, en lecture (spécification du 16/10, R14).
        'caisse:read',
        'activite_medecin:read',
    ],
    /**
     * ADR 0004 — UNE SEULE personne dans tout AFS.
     *
     * Référent médical de l'entreprise, responsable de PRISME, et seul à juger
     * si une commande est médicalement justifiée. Il détient tout ce que
     * détient `RESPONSABLE_SITE`.
     *
     * Sa validation médicale porte sur le RÉSEAU ENTIER, pas sur le site où il
     * se trouve ce jour-là : elle ne passe donc pas par la responsabilité de
     * site. C'est une exception assumée, écrite ici pour qu'elle ne ressemble
     * pas à un oubli.
     */
    MEDECIN_CHEF: [
        'site:read',
        'patient:read',
        'file_attente:read',
        'file_attente:write',
        'rdv:read',
        'dossier_medical:read',
        'dossier_medical:write',
        'stock:read',
        'commande:read',
        // Responsable de PRISME : il lit la caisse de son site, comme tout
        // responsable de site (R14) — et l'inclusion de l'ADR 0004 l'exige.
        'caisse:read',
        'commande:validate_medical',
        'activite_medecin:read',
    ],
    PHARMACIEN_FRONT: [
        'site:read',
        'patient:read',
        'stock:read',
        'dispensation:write',
        'inventaire:write',
        // Voit la ligne d'ordonnance, jamais le diagnostic — RG-PA-07.
    ],
    PHARMACIEN_BACK: [
        'site:read',
        'stock:read',
        'stock_central:write',
        'dispatch:write',
        'inventaire:write',
        'commande:read',
    ],
    RESPONSABLE_ACHAT: ['site:read', 'stock:read', 'commande:read', 'commande:write'],
    COMPTABLE: [
        'site:read',
        'adherent:read',
        'cotisation:read',
        'salaire:read', // décision 130 : le responsable financier
        'encaissement:write',
        'relance:read',
        'pec:read',
        'rapprochement:write',
        'dashboard:read',
        // Pas de encaissement:modify — RG-FIN-05 : Direction seule.
        // Pas de adherent:suspend — RG-FIN-08 : Direction et chargée (décision 364).
    ],
    CHARGE_CLIENTELE: [
        'site:read',
        'adherent:read',
        'adherent:write',
        'offre:write',
        'patient:read',
        // Réunion du 15/10 : elle crée elle-même une fiche — un salarié, un
        // conjoint ou un enfant avec sa pièce — sur la foi d'un courrier de
        // l'entreprise. Créée par elle, la fiche naît confirmée (voir `enroler`).
        'patient:write',
        'patient:depart',
        // Matrice CCT_01 § 2.1 : L sur « Accueil & Identification ». Décision du
        // Tech Lead, recette du 28/09 : elle confirme une fiche contre le DNS, elle
        // doit en voir le verdict. En ligne seulement : le coffre hors ligne reste
        // aux rôles de site (voir `hors-ligne.routes.ts`).
        'eligibilite:check',
        'patient:confirm',
        'patient:update',
        'ayant_droit:confirm_piece',
        'ayant_droit:retirer', // décision du 29/09 : elle seule, nativement
        'patient:merge',
        'file_attente:read',
        'rdv:read',
        'dns:read',
        'dns:import',
        'dns:validate',
        'cotisation:read',
        'salaire:read', // décision 130
        'relance:read',
        // Décision 364 (TL, 30/10), en attendant le module de recouvrement : elle
        // suspend une entreprise et lève la suspension quand la cotisation arrive.
        // La même permission que la Direction, donc la même règle et le même effet
        // à l'accueil ; un motif dans les deux sens. Aucun portefeuille ne borne
        // ses entreprises (la notion n'existe pas) : elle les a toutes. La
        // résiliation reste ce qu'elle était (`adherent:write`, CP-05).
        'adherent:suspend',
        'negociation:write',
        'dashboard:read',
        // Décision 458 : le registre d'un mois pour une entreprise, qu'elle lui remet.
        'registre:export_entreprise',
    ],
    AGENT_PEC: [
        'site:read',
        'patient:read',
        'eligibilite:check',
        'pec:read',
        'pec:emit',
        'pec:verify_qr',
        // Pas de pec:derogation — RG-PEC-05.
        // Pas de pec:config — réservé au SUPER_ADMIN.
    ],
    /**
     * Spécification du 16/10, R16 — rôle de site : il exige un site sur le
     * compte. Retrouver la fiche, encaisser, clôturer ; rien de médical, pas
     * même le verdict d'éligibilité : la caisse dit seulement « à régler ».
     * Un caissier seul ne compte pas dans la dotation de l'accueil — celui qui
     * cumule les deux porte le dérivé natif `ACCUEIL_CAISSE` ci-dessous.
     */
    CAISSIER: ['site:read', 'patient:read', 'caisse:encaisser', 'caisse:cloturer'],
    /**
     * Décision 449 (D1 bis, D1 ter, D17) — le chef de centre non médecin, rôle
     * de site : il exige un site sur le compte. Il gère les comptes de son
     * centre (`utilisateur:write_centre`, qui ne sert qu'avec la désignation
     * sur le site) et lit la file et la caisse de son centre. Ni fiche patient,
     * ni dossier, ni activité des médecins (décision 168), ni alerte de
     * l'accueil, ni dotation.
     */
    CHEF_CENTRE: ['site:read', 'utilisateur:write_centre', 'file_attente:read', 'caisse:read'],
    // Portail adhérent (Phase 3) : aucune permission tant qu'un compte n'est
    // pas rattaché à son entreprise et que le serveur ne filtre pas par elle
    // — voir `ROLES_NON_OUVERTS` (décision 115).
    RH_ADHERENT: [],
    PRESTATAIRE: [
        // Portail prestataire (Phase 3). Vérification QR uniquement (RG-PEC-03).
        'pec:verify_qr',
    ],
};
export function roleADroit(role, permission) {
    return PERMISSIONS_PAR_ROLE[role].includes(permission);
}
/**
 * Décision 418 — le rôle de base du Super administrateur : un seul compte actif
 * le porte (refus 409 à la création et à l'attribution), il détient toutes
 * les permissions, et aucun rôle ne se dérive de lui.
 */
export const ROLE_SUPER_ADMIN = 'SUPER_ADMIN';
export function estSuperAdmin(base) {
    return base === ROLE_SUPER_ADMIN;
}
/**
 * LE Super administrateur : un compte de base SUPER_ADMIN qui porte le rôle
 * NATIF (`roleCode` nul ou `SUPER_ADMIN`). Un compte resté sur un rôle dérivé
 * de cette base, d'avant la décision 418, n'en est pas un : il n'a que les
 * permissions de son dérivé, s'administre par le Super administrateur, mais
 * compte dans l'unicité (revue S1041, A2).
 */
export function estLeSuperAdmin(compte) {
    return estSuperAdmin(compte.role) && (compte.roleCode ?? compte.role) === ROLE_SUPER_ADMIN;
}
/**
 * Un rôle de cette base se propose-t-il à l'attribution, sur l'écran ? Ni un
 * rôle non ouvert (décision 115), ni le Super administrateur : son compte
 * unique se crée par le script d'exploitation (décision 418).
 */
export function roleProposeALAttribution(base) {
    return motifRoleNonOuvert(base) === null && !estSuperAdmin(base);
}
/** Le refus (409) d'un second Super administrateur actif, au serveur comme au script. */
export const MOTIF_SUPER_ADMIN_UNIQUE = 'Un Super administrateur actif existe déjà : la plateforme n’en a qu’un.';
/**
 * Le nom du verrou transactionnel sous lequel on compte les Super
 * administrateurs avant d'en créer, d'en attribuer ou d'en réactiver un :
 * deux créations simultanées ne passent pas toutes deux le compte. Le même
 * pour l'API et pour le script d'exploitation (`prisma/super-admin.ts`).
 */
export const VERROU_SUPER_ADMIN_UNIQUE = 'utilisateur:super_admin_unique';
/**
 * Décisions 214 et 220 : le tableau de bord du jour, Direction seule — et le
 * Super administrateur, qui peut tout (décision 418). Lu par l'écran (menu,
 * garde de route) ; l'API dit la même chose par `roleOuSuperAdmin`.
 */
export const ROLES_TABLEAU_DE_BORD = ['DIRECTION', ROLE_SUPER_ADMIN];
/**
 * Un geste que le code réserve à certains rôles de base, par DROIT (« la
 * Direction seule change le son ») : le Super administrateur le tient aussi
 * (décision 418). À ne pas employer pour une IDENTITÉ — « le praticien qui
 * tient ce box », « l'accueil de ce centre » : le Super administrateur n'est
 * ni l'un ni l'autre, et ces gardes ne passent pas par ici.
 */
export function roleOuSuperAdmin(base, roles) {
    return estSuperAdmin(base) || roles.includes(base);
}
/**
 * Décision 432 (TL, 31/10) — LE Super administrateur fait aussi les gestes du
 * praticien (Ma file, box, appel, consultation, urgence, prix du dentiste),
 * pour essayer la plateforme sans changer de compte. Le compte natif seul,
 * jamais un dérivé de sa base ni la Direction. Il agit en son nom : le box,
 * l'appel et la consultation portent son identifiant, comme pour un médecin.
 */
export function faitLesGestesDuPraticien(compte, 
/** `FONCTION_SUPER_ADMIN_PARTOUT` (décision 436) : éteinte, le corps médical seul, comme en v1.0.1. */
superAdminPartout) {
    return ROLES_MEDICAUX.includes(compte.role) || (superAdminPartout && estLeSuperAdmin(compte));
}
/**
 * Le masque de l'écran (menu, garde de route) des gestes du praticien, lu sur
 * le rôle de base comme `ROLES_TABLEAU_DE_BORD` ; le serveur décide par
 * `faitLesGestesDuPraticien`.
 */
export const ROLES_GESTES_DU_PRATICIEN = [...ROLES_MEDICAUX, ROLE_SUPER_ADMIN];
export function exige2fa(role) {
    return ROLES_2FA_OBLIGATOIRE.includes(role);
}
export function estRoleTransverse(role) {
    return ROLES_TRANSVERSES.includes(role);
}
export function estRoleDeSite(role) {
    return ROLES_DE_SITE.includes(role);
}
export function estRoleExterne(role) {
    return ROLES_EXTERNES.includes(role);
}
/**
 * Libellés français des rôles.
 *
 * Ici et non dans le front : ces libellés partent aussi dans les exports
 * Direction et les documents imprimés produits par l'API. Deux tables de
 * traduction finiraient par diverger, et c'est l'utilisateur qui verrait la
 * différence entre son écran et son PDF.
 *
 * Ce sont les termes du CCT et du vocabulaire AFS, pas une traduction libre
 * de l'identifiant technique (CLAUDE.md § 9).
 */
export const LIBELLE_ROLE = {
    SUPER_ADMIN: 'Super administrateur',
    DIRECTION: 'Direction',
    AGENT_ACCUEIL: "Agent d'accueil",
    MEDECIN: 'Médecin',
    RESPONSABLE_SITE: 'Responsable de site',
    MEDECIN_CHEF: 'Médecin chef',
    PHARMACIEN_FRONT: 'Pharmacien comptoir',
    PHARMACIEN_BACK: 'Pharmacien back-office',
    RESPONSABLE_ACHAT: 'Responsable des achats',
    COMPTABLE: 'Comptable',
    CHARGE_CLIENTELE: 'Chargé de clientèle',
    AGENT_PEC: 'Agent PEC',
    CAISSIER: 'Caissière',
    CHEF_CENTRE: 'Responsable d’antenne',
    RH_ADHERENT: 'RH adhérent',
    PRESTATAIRE: 'Prestataire',
};
/**
 * Décision 449 — les deux rôles du chef de centre, seuls à pouvoir porter
 * `utilisateur:write_centre` (RG-SEC-ROLE-CENTRE) : le dérivé natif
 * ci-dessous, et `CHEF_CENTRE`, le rôle de base du chef qui n'est pas
 * médecin (migrations 20261102090000 et 20261102090100).
 */
export const CODE_MEDECIN_CHEF_CENTRE = 'MEDECIN_CHEF_CENTRE';
export const CODE_CHEF_CENTRE = 'CHEF_CENTRE';
export const CODES_CHEF_DE_CENTRE = [CODE_MEDECIN_CHEF_CENTRE, CODE_CHEF_CENTRE];
export const ROLES_DERIVES_NATIFS = [
    {
        code: 'ACCUEIL_CAISSE',
        base: 'AGENT_ACCUEIL',
        libelle: 'Accueil + caisse',
        permissions: [...PERMISSIONS_PAR_ROLE.AGENT_ACCUEIL, 'caisse:encaisser', 'caisse:cloturer'],
    },
    {
        // Décision 449 — le chef de centre médecin (cas d'Andraharo). Sa base
        // reste MEDECIN : Ma file, le box, la dotation et l'activité du jour ne
        // changent pas pour lui. Seule la gestion des comptes de son centre
        // s'ajoute (ADR 0004 et 0006 révisées le 02/11).
        code: CODE_MEDECIN_CHEF_CENTRE,
        base: 'MEDECIN',
        libelle: 'Médecin + responsable d’antenne',
        permissions: [...PERMISSIONS_PAR_ROLE.MEDECIN, 'utilisateur:write_centre'],
    },
];
/**
 * Les codes qu'un rôle libre ne prend jamais (revue S1105, A2) : les natifs,
 * de base et dérivés, et les deux codes du chef de centre, même avant que
 * `CHEF_CENTRE` n'existe en base. Sinon un libellé « Chef centre » créerait
 * le code avant le natif, et la migration du natif tomberait sur lui.
 */
export const CODES_DE_ROLE_RESERVES = [
    ...new Set([...ROLES, ...ROLES_DERIVES_NATIFS.map((role) => role.code), ...CODES_CHEF_DE_CENTRE]),
];
/** La matrice du code d'un rôle natif — de base ou dérivé — par son code ; `null` sinon. */
export function matriceDuCode(code) {
    const derive = ROLES_DERIVES_NATIFS.find((role) => role.code === code);
    if (derive !== undefined)
        return derive.permissions;
    return ROLES.includes(code) ? PERMISSIONS_PAR_ROLE[code] : null;
}
//# sourceMappingURL=roles.js.map